import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../api";
import { formatDate } from "../utils/dateUtils";
import NavBar from "../components/NavBar";
import {
  ResetIcon,
  FilterIcon,
  SendIcon,
} from "../components/icons";
import { ClientGridSkeleton } from "../components/Skeleton";

// "Matches Tracking" index page — grouped by client.
// Each card represents one client and rolls up:
//   - total matches the bureau has created for that client
//   - per-status pill counts (sent / accepted / rejected / etc.)
//   - the most recent match date
// Clicking a card routes to /bureau/referrals/:clientId, which renders
// the full per-referral table for that one client.
//
// We rely on the backend aggregation at /referrals/bureau/by-client so
// the dataset stays bounded by client-count, not referral-count. Without
// that endpoint we'd be pulling every paginated page of referrals and
// grouping in JS — fine at 10 referrals, painful at 10 000.

const STATUS_BADGE_COLOR = {
  sent: "bg-blue-100 text-blue-800",
  viewed: "bg-yellow-100 text-yellow-800",
  contacted: "bg-purple-100 text-purple-800",
  interested: "bg-green-100 text-green-800",
  accepted: "bg-emerald-100 text-emerald-800",
  rejected: "bg-red-100 text-red-800",
};

const ACTIVE_STATUSES = new Set(["sent", "viewed", "contacted", "interested"]);

const ReferralManagement = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchName, setSearchName] = useState("");
  const [hasStatus, setHasStatus] = useState("");

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [byClient, statsResp] = await Promise.all([
        apiFetch("/referrals/bureau/by-client"),
        apiFetch("/referrals/bureau/stats"),
      ]);
      setRows(byClient.data || []);
      setStats(statsResp.data || null);
    } catch (err) {
      console.error("Failed to fetch matches:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Local filtering — the dataset here is at most one row per client,
  // so JS-side filter is fine and snappier than re-fetching.
  const filteredRows = useMemo(() => {
    const term = searchName.trim().toLowerCase();
    return rows.filter((r) => {
      if (term) {
        const hay = [
          r.client?.fullName || "",
          r.client?.referenceId || "",
          r.client?.contactNumber || "",
        ]
          .join(" ")
          .toLowerCase();
        if (!hay.includes(term)) return false;
      }
      if (hasStatus && !(r.byStatus && r.byStatus[hasStatus] > 0)) return false;
      return true;
    });
  }, [rows, searchName, hasStatus]);

  const handleReset = () => {
    setSearchName("");
    setHasStatus("");
  };

  if (loading && rows.length === 0) {
    return (
      <div className="min-h-screen bg-[var(--color-background)]">
        <NavBar />
        <div className="max-w-7xl mx-auto px-4 py-6">
          <ClientGridSkeleton count={6} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      <NavBar />

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div className="flex items-center gap-3 min-w-0">
            <span className="w-9 h-9 shrink-0 rounded-lg bg-[var(--color-primary-soft)] text-[var(--color-primary)] flex items-center justify-center">
              <SendIcon className="w-5 h-5" />
            </span>
            <div className="min-w-0">
              <h1 className="text-base sm:text-lg font-semibold text-[var(--color-text-strong)] truncate">
                Matches Tracking
              </h1>
              <p className="text-xs text-[var(--color-text-muted)]">
                One card per client — click any client to see every match
                you've created for them.
              </p>
            </div>
          </div>
        </div>

        {/* Stats — same as before but the labels make more sense in
            the per-client framing now. */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg p-3">
              <div className="text-xl font-bold text-[var(--color-primary)]">
                {rows.length}
              </div>
              <div className="text-xs uppercase tracking-wider font-semibold text-[var(--color-text-muted)] mt-0.5">
                Clients With Matches
              </div>
            </div>
            <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg p-3">
              <div className="text-xl font-bold text-[var(--color-primary)]">
                {stats.totalReferrals}
              </div>
              <div className="text-xs uppercase tracking-wider font-semibold text-[var(--color-text-muted)] mt-0.5">
                Total Matches
              </div>
            </div>
            <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg p-3">
              <div className="text-xl font-bold text-blue-500">
                {stats.activeReferrals}
              </div>
              <div className="text-xs uppercase tracking-wider font-semibold text-[var(--color-text-muted)] mt-0.5">
                Active
              </div>
            </div>
            <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg p-3">
              <div className="text-xl font-bold text-green-500">
                {stats.statusBreakdown?.find((s) => s._id === "accepted")
                  ?.count || 0}
              </div>
              <div className="text-xs uppercase tracking-wider font-semibold text-[var(--color-text-muted)] mt-0.5">
                Accepted
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[var(--color-text-strong)] mb-1">
                Search by client
              </label>
              <input
                type="text"
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
                placeholder="Name, reference ID, or phone..."
                className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-[var(--color-background)] text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--color-text-strong)] mb-1">
                Has match with status
              </label>
              <select
                value={hasStatus}
                onChange={(e) => setHasStatus(e.target.value)}
                className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-[var(--color-background)] text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
              >
                <option value="">Any status</option>
                <option value="sent">Sent</option>
                <option value="viewed">Viewed</option>
                <option value="contacted">Contacted</option>
                <option value="interested">Interested</option>
                <option value="accepted">Accepted</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button
              onClick={handleReset}
              className="whitespace-nowrap inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-[var(--color-border)] text-[var(--color-text)] hover:bg-[var(--color-surface-hover)] transition-colors text-sm font-medium"
            >
              <ResetIcon className="w-4 h-4" />
              Reset
            </button>
            <button
              type="button"
              onClick={fetchData}
              className="whitespace-nowrap inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] text-[var(--color-on-primary)] transition-colors text-sm font-semibold"
            >
              <FilterIcon className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>

        {error && <div className="text-red-500 text-center py-4">{error}</div>}

        {/* Client cards */}
        {filteredRows.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-[var(--color-text-muted)] text-lg mb-2">
              {rows.length === 0
                ? "No matches yet"
                : "No clients match those filters"}
            </div>
            {rows.length === 0 && (
              <div className="text-[var(--color-text-muted)]">
                Open a client from Client Management and create their first
                match.
              </div>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredRows.map((row) => {
              const activeCount = Object.entries(row.byStatus || {}).reduce(
                (n, [s, c]) => n + (ACTIVE_STATUSES.has(s) ? c : 0),
                0
              );
              const acceptedCount = row.byStatus?.accepted || 0;
              const rejectedCount = row.byStatus?.rejected || 0;
              return (
                <button
                  key={row.clientId}
                  type="button"
                  onClick={() =>
                    navigate(`/bureau/referrals/${row.clientId}`)
                  }
                  className="text-left bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg p-4 hover:border-[var(--color-primary)] hover:shadow-md transition-all"
                >
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="min-w-0">
                      <div className="text-base font-semibold text-[var(--color-text-strong)] truncate">
                        {row.client?.fullName || "(Unnamed)"}
                      </div>
                      <div className="text-xs text-[var(--color-text-muted)] mt-0.5 truncate">
                        {row.client?.referenceId || "—"}
                        {row.client?.city ? ` • ${row.client.city}` : ""}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-2xl font-bold text-[var(--color-primary)] leading-none">
                        {row.totalReferrals}
                      </div>
                      <div className="text-[10px] uppercase tracking-wider font-semibold text-[var(--color-text-muted)] mt-1">
                        Matches
                      </div>
                    </div>
                  </div>

                  {/* Status pills — only show the buckets the client
                      actually has, so the card doesn't get noisy. */}
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {Object.entries(row.byStatus || {})
                      .sort((a, b) => b[1] - a[1])
                      .map(([status, count]) => (
                        <span
                          key={status}
                          className={`inline-flex items-center px-2 py-0.5 text-[11px] font-medium rounded-full ${
                            STATUS_BADGE_COLOR[status] ||
                            "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {count} {status}
                        </span>
                      ))}
                  </div>

                  <div className="flex items-center justify-between text-xs text-[var(--color-text-muted)] pt-3 border-t border-[var(--color-border)]">
                    <span>
                      {activeCount} active · {acceptedCount} accepted ·{" "}
                      {rejectedCount} rejected
                    </span>
                    <span>
                      {row.lastReferralAt
                        ? formatDate(row.lastReferralAt)
                        : ""}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReferralManagement;
